import { cn } from '../../utils/cn';

interface SwitchProps {
  /** Current on/off state */
  checked: boolean;
  /** Callback when toggled */
  onChange: (checked: boolean) => void;
  /** Label text rendered next to the switch */
  label?: string;
  /** Accessible name when no visible label is rendered (for example a toggle inside a table row) */
  'aria-label'?: string;
  /** Disable interactions */
  disabled?: boolean;
  /** HTML id for the switch button */
  id?: string;
  /** Additional CSS classes on the wrapper */
  className?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  'aria-label': ariaLabel,
  disabled = false,
  id,
  className,
}: SwitchProps) {
  const switchId = id || label?.toLowerCase().replace(/\s+/g, '-');

  return (
    // Touch target below lg lives on the clickable label; the track keeps its h-6 w-11 shape.
    <label
      htmlFor={switchId}
      className={cn(
        'inline-flex items-center gap-2 select-none min-h-[var(--touch-target-min)] lg:min-h-0',
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
    >
      <button
        id={switchId}
        role="switch"
        type="button"
        aria-checked={checked}
        aria-label={ariaLabel ?? label}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus',
          checked ? 'bg-accent-500' : 'bg-border-strong',
          disabled && 'pointer-events-none',
        )}
      >
        <span
          className={cn(
            'inline-block h-4 w-4 rounded-full bg-white shadow-xs transition-transform duration-200',
            checked ? 'translate-x-6' : 'translate-x-1',
          )}
        />
      </button>
      {label && (
        <span className="text-sm text-text-primary">{label}</span>
      )}
    </label>
  );
}

export default Switch;
